import React, { useState } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import MultiFileUpload, { FileWithType } from './MultiFileUpload';
import {
  RiArrowLeftLine as BackIcon,
  RiAddLine as AddIcon,
  RiDeleteBinLine as DeleteIcon,
  RiSparkling2Line as MagicIcon,
} from 'react-icons/ri';
import type { BatchLetterConfig } from '@/types/letters';

interface BatchLetterConfigWithFiles extends BatchLetterConfig {
  files?: FileWithType[];
}

interface BatchLetterCreateFormProps {
  onCancel: () => void;
  onCreate: (letters: BatchLetterConfigWithFiles[], commonSettings: {
    common_instructions?: string;
    common_whitelist?: string;
    common_blacklist?: string;
    common_files?: FileWithType[];
  }) => void | Promise<void>;
  isSubmitting?: boolean;
}

const BatchLetterCreateForm: React.FC<BatchLetterCreateFormProps> = ({
  onCancel,
  onCreate,
  isSubmitting,
}) => {
  const [letters, setLetters] = useState<BatchLetterConfigWithFiles[]>([
    { referee_name: '', files: [] },
    { referee_name: '', files: [] },
    { referee_name: '', files: [] },
  ]);
  const [commonInstructions, setCommonInstructions] = useState<string>('');
  const [commonWhitelist, setCommonWhitelist] = useState<string>('');
  const [commonBlacklist, setCommonBlacklist] = useState<string>('');
  const [commonFiles, setCommonFiles] = useState<FileWithType[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});

  const handleAddLetter = () => {
    if (letters.length >= 20) {
      return; // Max 20 letters
    }
    setLetters([...letters, { referee_name: '', files: [] }]);
  };

  const handleRemoveLetter = (index: number) => {
    if (letters.length <= 1) {
      return; // Keep at least one
    }
    setLetters(letters.filter((_, i) => i !== index));
    // Remove error for this index
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const handleLetterChange = (
    index: number,
    field: keyof BatchLetterConfigWithFiles,
    value: string | FileWithType[]
  ) => {
    const updated = [...letters];
    updated[index] = { ...updated[index], [field]: value };
    setLetters(updated);

    // Clear error for this letter when user types
    if (field === 'referee_name' && errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const handleLetterFilesChange = (index: number, files: FileWithType[]) => {
    const updated = [...letters];
    updated[index] = { ...updated[index], files };
    setLetters(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all letters have referee names
    const newErrors: Record<number, string> = {};
    letters.forEach((letter, index) => {
      if (!letter.referee_name.trim()) {
        newErrors[index] = 'Referee name is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clean up letters - remove empty optional fields
    const cleanedLetters = letters.map(letter => ({
      referee_name: letter.referee_name.trim(),
      ...(letter.custom_instructions?.trim() && {
        custom_instructions: letter.custom_instructions.trim(),
      }),
      ...(letter.whitelist?.trim() && {
        whitelist: letter.whitelist.trim(),
      }),
      ...(letter.blacklist?.trim() && {
        blacklist: letter.blacklist.trim(),
      }),
      ...(letter.focus_area?.trim() && {
        focus_area: letter.focus_area.trim(),
      }),
    }));

    const commonSettings = {
      ...(commonInstructions.trim() && {
        common_instructions: commonInstructions.trim(),
      }),
      ...(commonWhitelist.trim() && {
        common_whitelist: commonWhitelist.trim(),
      }),
      ...(commonBlacklist.trim() && {
        common_blacklist: commonBlacklist.trim(),
      }),
      ...(commonFiles.length > 0 && {
        common_files: commonFiles,
      }),
    };

    await onCreate(cleanedLetters, commonSettings);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="inline-flex items-center gap-1"
        >
          <BackIcon className="h-4 w-4" /> Back
        </Button>
        <h3 className="text-lg font-semibold text-neutral-900">
          Batch Generate Letters of Recommendation
        </h3>
      </div>

      <Card padding="lg">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Common Settings Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-neutral-700 border-b pb-2">
              Common Settings (Applied to All Letters)
            </h4>
            <div className="space-y-3">
              <Textarea
                label="Common Instructions"
                placeholder="Instructions applied to all letters (e.g., 'Maintain professional tone, emphasize international impact')"
                helperText="These instructions will be applied to every letter in the batch"
                value={commonInstructions}
                onChange={e => setCommonInstructions(e.target.value)}
                rows={2}
                fullWidth
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Textarea
                  label="Common Allowlist"
                  placeholder="Terms to emphasize in all letters"
                  helperText="Comma-separated"
                  value={commonWhitelist}
                  onChange={e => setCommonWhitelist(e.target.value)}
                  rows={2}
                  fullWidth
                />
                <Textarea
                  label="Common Blocklist"
                  placeholder="Terms to avoid in all letters"
                  helperText="Comma-separated"
                  value={commonBlacklist}
                  onChange={e => setCommonBlacklist(e.target.value)}
                  rows={2}
                  fullWidth
                />
              </div>
              <MultiFileUpload
                selectedFiles={commonFiles}
                onFilesChange={setCommonFiles}
                disabled={isSubmitting}
                maxFiles={10}
              />
              <p className="text-xs text-neutral-500 mt-2">
                Common files (e.g., beneficiary CV, case background) will be used for all letters
              </p>
            </div>
          </div>

          {/* Individual Letters Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b pb-2">
              <h4 className="text-sm font-semibold text-neutral-700">
                Letters Configuration ({letters.length})
              </h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddLetter}
                disabled={letters.length >= 20 || isSubmitting}
                className="inline-flex items-center gap-1"
              >
                <AddIcon className="h-4 w-4" />
                Add Letter
              </Button>
            </div>

            <div className="space-y-4">
              {letters.map((letter, index) => (
                <Card
                  key={index}
                  padding="md"
                  className="bg-neutral-50 border border-neutral-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h5 className="text-sm font-medium text-neutral-700">
                      Letter #{index + 1}
                    </h5>
                    {letters.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveLetter(index)}
                        disabled={isSubmitting}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <DeleteIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Input
                      label="Referee Name"
                      placeholder="e.g., Dr. John Smith"
                      value={letter.referee_name}
                      onChange={e =>
                        handleLetterChange(index, 'referee_name', e.target.value)
                      }
                      error={errors[index]}
                      helperText="Required"
                      fullWidth
                      disabled={isSubmitting}
                    />

                    <Textarea
                      label="Focus Area (Optional)"
                      placeholder="e.g., 'Technical expertise and research impact'"
                      value={letter.focus_area || ''}
                      onChange={e =>
                        handleLetterChange(index, 'focus_area', e.target.value)
                      }
                      rows={2}
                      fullWidth
                      disabled={isSubmitting}
                    />

                    <Textarea
                      label="Custom Instructions (Optional)"
                      placeholder="Specific instructions for this letter"
                      value={letter.custom_instructions || ''}
                      onChange={e =>
                        handleLetterChange(index, 'custom_instructions', e.target.value)
                      }
                      rows={2}
                      fullWidth
                      disabled={isSubmitting}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Textarea
                        label="Allowlist (Optional)"
                        placeholder="Additional terms for this letter"
                        value={letter.whitelist || ''}
                        onChange={e =>
                          handleLetterChange(index, 'whitelist', e.target.value)
                        }
                        rows={1}
                        fullWidth
                        disabled={isSubmitting}
                      />
                      <Textarea
                        label="Blocklist (Optional)"
                        placeholder="Terms to avoid in this letter"
                        value={letter.blacklist || ''}
                        onChange={e =>
                          handleLetterChange(index, 'blacklist', e.target.value)
                        }
                        rows={1}
                        fullWidth
                        disabled={isSubmitting}
                      />
                    </div>

                    <MultiFileUpload
                      selectedFiles={letter.files || []}
                      onFilesChange={files => handleLetterFilesChange(index, files)}
                      disabled={isSubmitting}
                      maxFiles={5}
                    />
                    <p className="text-xs text-neutral-500">
                      Referee-specific files (e.g., referee CV, questionnaire, collaboration docs)
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2"
            >
              <MagicIcon className="h-4 w-4" />
              <span>Generate {letters.length} Letters</span>
            </Button>
          </div>
        </form>
      </Card>

      <Card padding="sm" className="bg-blue-50 border-blue-200">
        <p className="text-xs text-blue-900">
          <strong>Tip:</strong> All letters will be generated in parallel. Each letter can have its own focus area and customization while sharing common settings.
        </p>
      </Card>
    </div>
  );
};

export default BatchLetterCreateForm;
